import React, { useContext, useEffect, useState } from "react";
import MainServices from "../../../../services/MainServices";
import { toast } from "react-toastify";
import { Field, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import CustomErrorMessage from "../../../../assets/common/CustomeErrorMessage";
import AsyncSelect from "react-select/async";
import Edit from "../../../../assets/images/icons/editIconNew.svg";
import ButtonLoading from "../../../../assets/loaders/ButtonLoading";
import dateIcon from "../../../../assets/images/dateIcon.svg";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";
import EditingRestrictionNotice from "./EditRestrictionNotice";

const EmploymentInformationNew = ({
  employmentInfo,
  handleGetTableData,
  empPublicId,
  getEmployeeRegistrationData,
  personalInfo,
  currentEmployeeEditing = false,
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [contractType, setContractType] = useState([]);
  const [employmentType, setEmploymentType] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [reportingTo, setReportingTo] = useState([]);
  const [probationOptions, setProbationOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [jobTitleOptions, setJobTitleOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jobGradeOptions, setJobGradeOptions] = useState([]);

  const {
    getJobTitleData,
    updateEmploymentInfo,
    fetchEmployeeEmployementInfo,
    getContractTypeMaster,
    getDepartmentMaster,
    getBranchMaster,
    getDesignationMaster,
    getEmployeesPersonalInfoMaster,
    getEmployeesMasterData,
    getRolesMaster,
    updateEmployeeRegistrationMaster,
    getJobGradeData,
  } = MainServices();

  useEffect(() => {
    getContractTypeOptions();
    getJobTitleOptions();
    getJobGradeOptions();
    getDepartmentOptions();
    getDesignationOptions();
    getReportingToOptions();
    getBranchOptions();
    getRoleOptions();
    getEmployementTypeOptions();
  }, []);

  const getBranchOptions = async () => {
    const filters = {
      pageSize: 500,
    };
    try {
      const response = await getBranchMaster(filters);
      console.log("Branch To options: ", response);
      if (response?.data?.data?.doc?.length >= 0) {
        setBranchOptions(
          response?.data?.data?.doc?.map((branch) => ({
            label: branch.name,
            value: branch.publicId,
          }))
        );
      } else {
        setBranchOptions([{ label: "Data not loaded", value: "" }]);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred"
      );
      console.error("Error", error);
    }
  };

  const loadBranchOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback(branchOptions);
    } else {
      const filteredOptions = branchOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      callback(filteredOptions);
    }
  };

  const getJobTitleOptions = async () => {
    const filters = {
      pageSize: 500,
    };
    try {
      const response = await getJobTitleData(filters);
      console.log("Job Title options: ", response);
      if (response?.data?.data?.doc?.length >= 0) {
        setJobTitleOptions(
          response?.data?.data?.doc?.map((jobTitle) => ({
            label: jobTitle.name,
            value: jobTitle.publicId,
          }))
        );
      } else {
        setJobTitleOptions([{ label: "Data not loaded", value: "" }]);
      }
    } catch (error) {
      toast.warn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
      console.error("Error", error);
    }
  };

  const loadJobTitleOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback(jobTitleOptions);
    } else {
      const filteredOptions = jobTitleOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      callback(filteredOptions);
    }
  };

  const getJobGradeOptions = async () => {
    const filters = {
      pageSize: 500,
    };
    try {
      const response = await getJobGradeData(filters); // Replace with your actual API function
      if (response?.data?.data?.doc?.length >= 0) {
        setJobGradeOptions(
          response?.data?.data?.doc?.map((jobGrade) => ({
            label: jobGrade.name,
            value: jobGrade.publicId,
          }))
        );
      } else {
        setJobGradeOptions([{ label: "Data not loaded", value: "" }]);
      }
    } catch (error) {
      toast.warn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
      console.error("Error", error);
    }
  };

  const loadJobGradeOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback(jobGradeOptions);
    } else {
      const filteredOptions = jobGradeOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      callback(filteredOptions);
    }
  };

  const getRoleOptions = async () => {
    const filters = {
      pageSize: 500,
    };
    try {
      const response = await getRolesMaster(filters);
      console.log("Role options: ", response);
      if (response?.data?.data?.doc?.length >= 0) {
        setRoleOptions(
          response?.data?.data?.doc?.map((role) => ({
            label: role?.role,
            value: role?.publicId,
          }))
        );
      } else {
        setRoleOptions([{ label: "Data not loaded", value: "" }]);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred"
      );
      console.error("Error", error);
    }
  };

  const loadRoleOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback(roleOptions);
    } else {
      const filteredOptions = roleOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      callback(filteredOptions);
    }
  };

  const getDepartmentOptions = async () => {
    const filters = {
      pageSize: 500,
    };
    try {
      const response = await getDepartmentMaster(filters);
      console.log("Department options: ", response);
      if (response?.data?.data?.doc?.length >= 0) {
        setDepartmentOptions(
          response?.data?.data?.doc?.map((department) => ({
            label: department.name,
            value: department.publicId,
          }))
        );
      } else {
        setDepartmentOptions([{ label: "Data not loaded", value: "" }]);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred"
      );
      console.error("Error", error);
    }
  };

  const loadDepartmentOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback(departmentOptions);
    } else {
      const filteredOptions = departmentOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      callback(filteredOptions);
    }
  };

  const getDesignationOptions = async () => {
    const filters = {
      pageSize: 500,
    };
    try {
      const response = await getDesignationMaster(filters);
      console.log("Designation options: ", response);
      if (response?.data?.data?.doc?.length >= 0) {
        setDesignationOptions(
          response?.data?.data?.doc?.map((designation) => ({
            label: designation.name,
            value: designation.publicId,
          }))
        );
      } else {
        setDesignationOptions([{ label: "Data not loaded", value: "" }]);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred"
      );
      console.error("Error", error);
    }
  };

  const loadDesignationOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback(designationOptions);
    } else {
      const filteredOptions = designationOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      callback(filteredOptions);
    }
  };

  const getReportingToOptions = async () => {
    const filters = {
      pageSize: 500,
    };
    try {
      const response = await getEmployeesMasterData(filters); // Replace with actual API function
      console.log("Reporting To options: ", response);
      if (response?.data?.data?.doc?.length >= 0) {
        setReportingTo(
          response?.data?.data?.doc?.map((manager) => ({
            label: manager.username,
            value: manager.publicId,
          }))
        );
      } else {
        setReportingTo([{ label: "Data not loaded", value: "" }]);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred"
      );
      console.error("Error", error);
    }
  };

  const loadReportingToOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback(reportingTo);
    } else {
      const filteredOptions = reportingTo.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      callback(filteredOptions);
    }
  };

  const getContractTypeOptions = async () => {
    const dummyContractTypes = [
      { label: "Limited", value: "Limited" },
      { label: "Unlimited", value: "Unlimited" },
    ];
    setContractType(dummyContractTypes);
    // try {
    //   const response = await getContractTypeMaster();
    //   console.log("Contract Type options: ", response);
    //   if (response?.data?.data?.doc?.length >= 0) {
    //     setContractType(
    //       response?.data?.data?.doc?.map((type) => ({
    //         label: type.name,
    //         value: type.publicId,
    //       }))
    //     );
    //   } else {
    //     setContractType([{ label: "Data not loaded", value: "" }]);
    //   }
    // } catch (error) {
    //   console.error(error);
    //   setContractType([{ label: "Data not loaded", value: "" }]);
    // }
  };
  const loadContractTypeOptions = (inputValue, callback) => {
    const filteredOptions = contractType.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(inputValue ? filteredOptions : contractType);
  };

  const getEmployementTypeOptions = async () => {
    const dummyEmploymentTypes = [
      { label: "Full Time", value: "Full Time" },
      { label: "Part Time", value: "Part Time" },
      { label: "Distance", value: "Distance" },
    ];
    setEmploymentType(dummyEmploymentTypes);

    // try {
    //   const response = await getEmployementTypeMaster();
    //   console.log("Employment Type options: ", response);
    //   if (response?.data?.data?.doc?.length >= 0) {
    //     setEmploymentType(
    //       response?.data?.data?.doc?.map((type) => ({
    //         label: type.name,
    //         value: type.publicId,
    //       }))
    //     );
    //   } else {
    //     setEmploymentType([{ label: "Data not loaded", value: "" }]);
    //   }
    // } catch (error) {
    //   console.error(error);
    //   setEmploymentType([{ label: "Data not loaded", value: "" }]);
    // }
  };

  const loadEmploymentTypeOptions = (inputValue, callback) => {
    const filteredOptions = employmentType.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(inputValue ? filteredOptions : employmentType);
  };

  const getValidationSchema = () => {
    return Yup.object().shape({
      employeeId: Yup.string()
        .required("Employee ID is required")
        .matches(
          /^(?=.*[0-9])[A-Za-z0-9_-]+$/, // Must contain at least one number
          "Employee ID must be alphanumeric (include at least 1 number)"
        )
        .min(4, "Employee ID must be at least 4 characters")
        .max(20, "Employee ID cannot exceed 20 characters"),
      branch: Yup.object().required("Branch is required"),
      role: Yup.object().required("Role is required"),
      jobTitle: Yup.object().required("Job Title is required"),
      designation: Yup.object().required("Designation is required"),
      department: Yup.object().required("Department is required"),
      reportingTo: Yup.object().required("Reporting To is required"),
      joiningDate: Yup.date()
        .required("Joining Date is required")
        .typeError("Invalid date format"),
      jobGrade: Yup.object().required("Job Grade is required"),
      contractType: Yup.object().required("Contract Type is required"),
      probationPeriod: Yup.string().required("Probation Period is required"),
      employmentType: Yup.object().required("Employment Type is required"),
    });
  };

  const handleSubmit = async (values) => {
    const updatedEmploymentInfo = {
      ...(values?.employeeId && { employeeId: values.employeeId }),
      ...(values?.branch?.value && { branch: values.branch.value }),
      ...(values?.role?.value && { roleId: values.role.value }),
      ...(values?.jobTitle?.value && { jobTitle: values.jobTitle?.value }),
      ...(values?.designation?.value && {
        designation: values.designation.value,
      }),
      ...(values?.department?.value && {
        department: values.department.value,
      }),
      ...(values?.reportingTo?.value && {
        reportingTo: values.reportingTo.value,
      }),
      ...(values?.joiningDate && { joiningDate: values.joiningDate }),
      ...(values?.jobGrade?.value && { jobGrade: values.jobGrade.value }),
      ...(values?.contractType?.value && {
        contractType: values.contractType.value,
      }),
      ...(values?.probationPeriod && {
        probationPeriod: values.probationPeriod,
      }),
      ...(values?.employmentType?.value && {
        employeeType: values.employmentType.value,
      }),
    };
    setLoading(true);
    let formattedPersonalInfo = {
      ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
      ...(personalInfo?.secondName && { secondName: personalInfo.secondName }),
      ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
      ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
      ...(personalInfo?.phone && { phone: personalInfo.phone }),
      ...(personalInfo?.email && { email: personalInfo.email }),
      ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
      ...(personalInfo?.dobGregorian && {
        dobGregorian: personalInfo.dobGregorian,
      }),
      ...(personalInfo?.religion && {
        religion: personalInfo.religion,
      }),
      ...(personalInfo?.maritalStatus && {
        maritalStatus: personalInfo.maritalStatus,
      }),
      ...(personalInfo?.gender && { gender: personalInfo.gender }),
      ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

      ...(personalInfo?.citizen === "false" &&
        personalInfo?.nationality && {
          nationality: personalInfo?.nationality,
        }),
      ...(personalInfo?.citizen === "true" &&
        personalInfo?.nationalId && {
          nationalId: personalInfo.nationalId,
        }),
    };
    const details = {
      ...(formattedPersonalInfo && formattedPersonalInfo),
      employeeInfo: updatedEmploymentInfo,
    };
    try {
      const response = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (response?.data) {
        setIsEditing(false);
        toast("Employment Data Updated Successfully!", {
          style: { zIndex: 9999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
      } else {
        toast.error("Failed to update designation", {
          style: { zIndex: 9999 },
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred",
        {
          style: { zIndex: 9999 },
        }
      );
      console.error("Error", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";

    const isISODate = /^\d{4}-\d{2}-\d{2}T/.test(date);
    const isDDMMYYYY = /^\d{2}\/\d{2}\/\d{4}$/.test(date);
    const isYYYYMMDD = /^\d{4}-\d{2}-\d{2}$/.test(date);

    let formattedDate = "";

    if (isISODate) {
      formattedDate = new Date(date).toISOString().split("T")[0];
    } else if (isDDMMYYYY) {
      const [day, month, year] = date.split("/");
      formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(
        2,
        "0"
      )}`;
    } else if (isYYYYMMDD) {
      formattedDate = date;
    }

    return formattedDate;
  };

  const formatDateToDotFormat = (dateString) => {
    if (!dateString) return ""; // Handle empty or invalid input

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return ""; // Handle empty or undefined values

    const date = new Date(dateString);

    // Convert to YYYY-MM-DD format
    return date.toISOString().split("T")[0];
  };

  // const getEmployementInfoById = async () => {
  //   try {
  //     const response = await fetchEmployeeEmployementInfo(formData?.publicId);
  //     console.log("fetched respones: ", response);
  //     if (response?.data) {
  //       const employmentInfo = response?.data?.data;
  //     }
  //   } catch (error) {
  //     console.log(error);
  //   }
  // };

  // const handleUpdateEmploymentInfo = async () => {
  //   const updateEmploymentData = FIELDS.reduce((acc, field) => {
  //     const value = formData[field.field]; // Get the value from employment data
  //     if (value !== undefined && value !== null) {
  //       acc[field.field] = value;
  //     }
  //     return acc;
  //   }, {});
  //   try {
  //     const response = await updateEmploymentInfo(
  //       updateEmploymentData,
  //       formData?.publicId,
  //       formData?.concurrencyStamp
  //     );
  //     console.log("response: ", response);
  //     if (response?.data) {
  //       toast("Employement Data Updated Successfully!", {
  //         style: { zIndex: 999999999999999 },
  //       });
  //       getEmployeeRegistrationData(empPublicId);
  //       handleGetTableData();
  //       setIsEditing(false);
  //     }
  //   } catch (error) {
  //     console.log(error);
  //   }
  // };

  const asyncSelectStyles = {
    control: (provided) => ({
      ...provided,
      width: "100%",
      height: "56px",
      padding: "0 6px",
      borderRadius: "8px",
      display: "flex",
      justifyContent: "space-between",
      background: "#f9fafb",
      border: "1px solid #eaecf0",
      fontFamily: "Poppins, sans-serif",
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "18px",
      textAlign: "left",
      color: "#111827",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#A0A0A0",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#04080b",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "8px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      zIndex: 3,
    }),
    option: (provided, state) => ({
      ...provided,
      background: state.isFocused ? "#f3f4f6" : "#fff",
      color: "#04080b",
      padding: "10px 16px",
      cursor: "pointer",
      fontSize: "12px",
      lineHeight: "18px",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "200px",
      overflowY: "auto",
    }),
  };

  const handleEditClick = () => {
    setIsEditing(true); // Enable edit mode
  };

  return (
    <>
      <div className="tab-section">
        <div className="tab-header-container">
          <h3 className="tab-heading">{t("Employment")}</h3>
          {currentEmployeeEditing ? (
            <EditingRestrictionNotice message={t("Read-only")} />
          ) : (
            !isEditing && (
              <div
                className="tab-header-edit-container"
                onClick={handleEditClick}
              >
                <img src={Edit} alt="edit icon" />
                <span className="edit-txt">{t("Edit")}</span>
              </div>
            )
          )}
        </div>
        <Formik
          enableReinitialize
          initialValues={{
            employeeId: employmentInfo?.employeeId || "",
            branch: employmentInfo?.branchInfo
              ? {
                  label: employmentInfo.branchInfo.name,
                  value: employmentInfo.branchInfo.publicId,
                }
              : {
                  label: employmentInfo?.branch,
                  value: employmentInfo?.branch,
                },
            role: employmentInfo?.roleInfo
              ? {
                  label: employmentInfo.roleInfo.role,
                  value: employmentInfo.roleInfo.publicId,
                }
              : "",
            jobTitle: employmentInfo?.jobTitleInfo
              ? {
                  label: employmentInfo.jobTitleInfo?.name,
                  value: employmentInfo.jobTitleInfo?.publicId,
                }
              : "",
            designation: employmentInfo?.designationInfo
              ? {
                  label: employmentInfo.designationInfo.name,
                  value: employmentInfo.designationInfo.publicId,
                }
              : "",
            department: employmentInfo?.departmentInfo
              ? {
                  label: employmentInfo.departmentInfo.name,
                  value: employmentInfo.departmentInfo.publicId,
                }
              : "",
            reportingTo: employmentInfo?.reportingToInfo
              ? {
                  label:
                    employmentInfo.reportingToInfo.firstName ||
                    employmentInfo.reportingToInfo.email,
                  value: employmentInfo.reportingToInfo.publicId,
                }
              : "",
            joiningDate: employmentInfo?.joiningDate || "",
            jobGrade: employmentInfo?.jobGradeInfo
              ? {
                  label: employmentInfo.jobGradeInfo?.name,
                  value: employmentInfo.jobGradeInfo?.publicId,
                }
              : "",
            contractType: employmentInfo?.contractType
              ? {
                  label: employmentInfo.contractType,
                  value: employmentInfo.contractType,
                }
              : "",
            probationPeriod: employmentInfo?.probationPeriod || "",
            employmentType: employmentInfo?.employeeType
              ? {
                  label: employmentInfo?.employeeType,
                  value: employmentInfo?.employeeType,
                }
              : employmentInfo?.employeeType || "",
          }}
          validationSchema={getValidationSchema()} // Your validation schema
          onSubmit={(values) => handleSubmit(values)} // You
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <div className="add-body-content-grid">
                {/* Employee ID */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="employeeId">
                        {t("Employee ID")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <input
                        id="employeeId"
                        name="employeeId"
                        className="add-input"
                        placeholder={t("Enter Employee ID")}
                        value={values?.employeeId}
                        onChange={(e) =>
                          setFieldValue("employeeId", e.target.value)
                        }
                      />
                    ) : (
                      <div className="display-value">{values.employeeId}</div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="employeeId"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Branch / Location */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="branch">
                        {t("Branch / Location")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <AsyncSelect
                        id="branch"
                        name="branch"
                        value={values.branch}
                        onChange={(selectedOption) =>
                          setFieldValue("branch", selectedOption)
                        }
                        loadOptions={loadBranchOptions}
                        defaultOptions={branchOptions}
                        styles={asyncSelectStyles}
                        cacheOptions
                        placeholder={t("Select Branch")}
                      />
                    ) : (
                      <div className="display-value">
                        {values.branch?.label || ""}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="branch"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Role */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="role">
                        {t("Role")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <AsyncSelect
                        id="role"
                        name="role"
                        value={values.role}
                        onChange={(selectedOption) =>
                          setFieldValue("role", selectedOption)
                        }
                        loadOptions={loadRoleOptions}
                        defaultOptions={roleOptions}
                        styles={asyncSelectStyles}
                        cacheOptions
                        placeholder={t("Select Role")}
                      />
                    ) : (
                      <div className="display-value">
                        {values.role?.label || ""}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="role"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Job Title */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="jobTitle">
                        {t("Job Title")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <AsyncSelect
                        id="jobTitle"
                        name="jobTitle"
                        value={values.jobTitle}
                        onChange={(selectedOption) =>
                          setFieldValue("jobTitle", selectedOption)
                        }
                        loadOptions={loadJobTitleOptions}
                        defaultOptions={jobTitleOptions}
                        styles={asyncSelectStyles}
                        cacheOptions
                        placeholder={t("Select Job Title")}
                      />
                    ) : (
                      <div className="display-value">
                        {values.jobTitle?.label}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="jobTitle"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Designation */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="designation">
                        {t("Designation")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <AsyncSelect
                        id="designation"
                        name="designation"
                        value={values.designation}
                        onChange={(selectedOption) =>
                          setFieldValue("designation", selectedOption)
                        }
                        loadOptions={loadDesignationOptions}
                        defaultOptions={designationOptions}
                        styles={asyncSelectStyles}
                        cacheOptions
                        placeholder={t("Select Designation")}
                      />
                    ) : (
                      <div className="display-value">
                        {values.designation?.label || ""}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="designation"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Department */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="department">
                        {t("Department")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <AsyncSelect
                        id="department"
                        name="department"
                        value={values.department}
                        onChange={(selectedOption) =>
                          setFieldValue("department", selectedOption)
                        }
                        loadOptions={loadDepartmentOptions}
                        defaultOptions={departmentOptions}
                        styles={asyncSelectStyles}
                        cacheOptions
                        placeholder={t("Select Department")}
                      />
                    ) : (
                      <div className="display-value">
                        {values.department?.label || ""}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="department"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Reporting To */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="reportingTo">
                        {t("Reporting To")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <AsyncSelect
                        id="reportingTo"
                        name="reportingTo"
                        value={values.reportingTo}
                        onChange={(selectedOption) =>
                          setFieldValue("reportingTo", selectedOption)
                        }
                        loadOptions={loadReportingToOptions}
                        defaultOptions={reportingTo}
                        styles={asyncSelectStyles}
                        cacheOptions
                        placeholder={t("Select Manager")}
                      />
                    ) : (
                      <div className="display-value">
                        {values.reportingTo?.label || ""}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="reportingTo"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Joining Date */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="joiningDate">
                        {t("Joining Date")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <div className="add-input-wrapper">
                        <input
                          type="date"
                          id="joiningDate"
                          name="joiningDate"
                          className={`add-wrap-input ${
                            !values?.joiningDate ||
                            values.joiningDate === "dd-mm-yyyy"
                              ? "empty"
                              : ""
                          }`}
                          placeholder="DD/MM/YYYY"
                          value={
                            values?.joiningDate
                              ? formatDateToYYYYMMDD(values.joiningDate)
                              : ""
                          }
                          onChange={(e) =>
                            setFieldValue("joiningDate", e.target.value)
                          }
                        />
                        <img
                          src={dateIcon}
                          alt="date icon"
                          className="add-date-input-in"
                        />
                      </div>
                    ) : (
                      <div className="display-value">
                        {formatDateToYYYYMMDD(values.joiningDate)}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="joiningDate"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Job Grade */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="jobGrade">
                        {t("Job Grade")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <AsyncSelect
                        id="jobGrade"
                        name="jobGrade"
                        value={values.jobGrade}
                        onChange={(selectedOption) =>
                          setFieldValue("jobGrade", selectedOption)
                        }
                        loadOptions={loadJobGradeOptions}
                        defaultOptions={jobGradeOptions}
                        styles={asyncSelectStyles}
                        cacheOptions
                        placeholder={t("Select Job Grade")}
                      />
                    ) : (
                      <div className="display-value">
                        {values.jobGrade?.label || ""}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="jobGrade"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Contract Type */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="contractType">
                        {t("Contract Type")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <AsyncSelect
                        id="contractType"
                        name="contractType"
                        value={values.contractType}
                        onChange={(selectedOption) =>
                          setFieldValue("contractType", selectedOption)
                        }
                        loadOptions={loadContractTypeOptions}
                        defaultOptions={contractType}
                        styles={asyncSelectStyles}
                        cacheOptions
                        placeholder={t("Select Contract Type")}
                      />
                    ) : (
                      <div className="display-value">
                        {values.contractType?.label || ""}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="contractType"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Probation Period */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="probationPeriod">
                        {t("Probation Period")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <input
                        id="probationPeriod"
                        name="probationPeriod"
                        className="add-input"
                        placeholder={t("Enter Probation Period in Days")}
                        type="number"
                        min="1"
                        max="180"
                        value={values?.probationPeriod}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value) && Number(value) <= 180) {
                            setFieldValue("probationPeriod", value);
                          }
                        }}
                      />
                    ) : (
                      <div className="display-value">
                        {values.probationPeriod}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="probationPeriod"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Employment Type */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="employmentType">
                        {t("Employment Type")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <AsyncSelect
                        id="employmentType"
                        name="employmentType"
                        value={values.employmentType}
                        onChange={(selectedOption) =>
                          setFieldValue("employmentType", selectedOption)
                        }
                        loadOptions={loadEmploymentTypeOptions}
                        defaultOptions={employmentType}
                        styles={asyncSelectStyles}
                        cacheOptions
                        placeholder={t("Select Employment Type")}
                      />
                    ) : (
                      <div className="display-value">
                        {values.employmentType?.label || ""}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="employmentType"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>
              </div>

              {/* Submit button (only visible in edit mode) */}
              {isEditing && (
                <div className="add-footer">
                  <span></span>
                  <div className="add-footer-right">
                    <button
                      type="button"
                      className="add-footer-cancel-btn"
                      onClick={() => setIsEditing(false)}
                    >
                      {t("Cancel")}
                    </button>
                    <button
                      type="submit"
                      className="add-footer-submit-btn"
                      disabled={loading}
                    >
                      {loading ? <ButtonLoading /> : t("Submit")}
                    </button>
                  </div>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default EmploymentInformationNew;
